import React, { useCallback, useState } from "react";
import { FormHelperText, TextField, useTheme } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles.tsx";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { useCompleteShippingAgencyTransfer } from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/hooks/useCompleteShippingAgencyTransfer";
import { useShipAgencyTransUploads } from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/hooks/useShipAgencyTransUploads";
import { useTranslation } from "react-i18next";
import {
  reasonSchema,
  REJECTION_REASON_MAX_LENGTH,
} from "./RejectShippingAgencyTransferDrawer/reasonSchema";

interface ShippingAgencyTransferDecisionDrawerProps {
  decision: "approve" | "reject";
  isOpen: boolean;
  transferId: string;
  onClose: (drawerName: string) => void;
  onSuccess?: (message: string) => Promise<void> | void;
}

const LANG_BASE = "vessel-call.shipping-agency-transfer";
const SERVICE = "bff-vessel-call";
const EXTERNAL_ATTACHMENT_FIELD = "shippingAgencyTransferExternalAttachment";
const INTERNAL_ATTACHMENT_FIELD = "shippingAgencyTransferInternalAttachment";
const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

interface AttachmentFieldProps {
  fieldName: string;
  label: string;
  testId: string;
  files: UploadEntry[];
  setFiles: (files: UploadEntry[]) => void;
  upload: ReturnType<typeof useShipAgencyTransUploads>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  allowedExtensions: string[];
  conditions: React.ComponentProps<typeof DocumentUpload>["conditions"];
}

const AttachmentField = ({
  fieldName,
  label,
  testId,
  files,
  setFiles,
  upload,
  error,
  setError,
  allowedExtensions,
  conditions,
}: AttachmentFieldProps) => {
  const { t } = useTranslation();

  return (
    <div className="mt-4">
      <DocumentUpload
        label={label}
        testId={testId}
        name={fieldName}
        maxFiles={1}
        isError={Boolean(error || upload.error)}
        validateDrop={(count) => (count > 1 ? t("upload.one-document") : null)}
        onValidationResult={setError}
        disabled={upload.isPending}
        allowedExtensions={allowedExtensions}
        conditions={conditions}
        files={files}
        setFiles={setFiles}
        uploadDescription={t("upload.dragOrDropDescription")}
        onUpload={(newFiles) => {
          if (newFiles.length > 1) {
            setError(t("upload.one-document"));
            return;
          }
          const file = newFiles[0];
          if (!file) return;
          setError(null);
          setFiles([file]);
          upload.mutate({ files: [file], documentType: fieldName });
        }}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
      {upload.error && <FormHelperText error>{upload.error.message}</FormHelperText>}
      {files[0] && !error && (
        <div className="flex-1 mt-4">
          <SingleFile
            file={files[0]}
            fieldName={fieldName}
            testId={testId}
            isLoading={upload.isPending && files[0] instanceof File}
            setFiles={() => {
              setError(null);
              setFiles([]);
              if (!upload.isPending) upload.reset();
            }}
          />
        </div>
      )}
    </div>
  );
};

const ShippingAgencyTransferDecisionDrawer: React.FC<ShippingAgencyTransferDecisionDrawerProps> = ({
  decision,
  isOpen,
  transferId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const isReject = decision === "reject";
  const drawerName = isReject ? "rejectDrawer" : "approveDrawer";
  const screenName = isReject
    ? "RejectShippingAgencyTransferDrawer"
    : "ApproveShippingAgencyTransferDrawer";
  const [primaryNote, setPrimaryNote] = useState("");
  const [internalNotes, setInternalNotes] = useState("");

  const theme = useTheme();
  const dividerBorder = `1px solid ${theme.palette.strokeColor?.secondary ?? "#BBC1C5"}`;

  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const [error, setError] = useState<string | null>(null);
  const rejectionOnlySchema = reasonSchema(t).pick({ reason: true });

  const validateReason = (reason: string) => {
    const result = rejectionOnlySchema.safeParse({ reason });
    setError(result.success ? null : (result.error.flatten().fieldErrors.reason?.[0] ?? null));
  };
  const fileProfiles = useFileUploadProfiles();
  const attachmentProfile = fileProfiles.sideDrawerApprovalFile;
  const allowedExtensions = attachmentProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  const externalAttachments = useUploadStore(
    (state) => state.filesByKey[EXTERNAL_ATTACHMENT_FIELD] ?? EMPTY_UPLOAD_ENTRIES,
  );
  const internalAttachments = useUploadStore(
    (state) => state.filesByKey[INTERNAL_ATTACHMENT_FIELD] ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const setExternalAttachments = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) clearFilesForKey(EXTERNAL_ATTACHMENT_FIELD);
      else setFilesForKey(EXTERNAL_ATTACHMENT_FIELD, files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const setInternalAttachments = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) clearFilesForKey(INTERNAL_ATTACHMENT_FIELD);
      else setFilesForKey(INTERNAL_ATTACHMENT_FIELD, files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const externalAttachmentUploads = useShipAgencyTransUploads(
    screenName,
    EXTERNAL_ATTACHMENT_FIELD,
  );
  const internalAttachmentUploads = useShipAgencyTransUploads(
    screenName,
    INTERNAL_ATTACHMENT_FIELD,
  );
  const [externalAttachmentError, setExternalAttachmentError] = useState<string | null>(null);
  const [internalAttachmentError, setInternalAttachmentError] = useState<string | null>(null);
  const resetAttachments = () => {
    setExternalAttachments([]);
    setInternalAttachments([]);
    externalAttachmentUploads.reset();
    internalAttachmentUploads.reset();
    setExternalAttachmentError(null);
    setInternalAttachmentError(null);
  };

  const clearForm = () => {
    setPrimaryNote("");
    setInternalNotes("");
    resetAttachments();
    setError(null);
  };

  const handleSuccess = async () => {
    await onSuccess?.(
      t(`${LANG_BASE}.notifications.transfer-${isReject ? "rejected" : "approved"}`),
    );
    clearForm();
    onClose(drawerName);
  };

  const handleError = (mutationError: Error & { response?: { data?: { detail?: string } } }) => {
    clearForm();
    onClose(drawerName);

    notify({
      title: t("common.error"),
      description:
        mutationError?.response?.data?.detail ??
        mutationError?.message ??
        t("common.something-went-wrong"),
      type: "error",
    });
  };

  const { completeShippingAgencyTransfer } = useCompleteShippingAgencyTransfer({
    handleSuccess,
    handleError,
  });

  const handleSubmit = async () => {
    if (externalAttachmentUploads.isPending || internalAttachmentUploads.isPending) {
      notify({ title: t("common.notification.please-wait-for-upload"), type: "warning" });
      return;
    }
    if (
      externalAttachments.some((file) => file instanceof File || !file.url) ||
      internalAttachments.some((file) => file instanceof File || !file.url)
    ) {
      notify({
        title: t("common.error"),
        description:
          externalAttachmentUploads.error?.message ??
          internalAttachmentUploads.error?.message ??
          t("common.something-went-wrong"),
        type: "error",
      });
      return;
    }
    let rejectionReason: string | undefined;
    if (isReject) {
      const validation = reasonSchema(t).safeParse({ reason: primaryNote });
      if (!validation.success) {
        setError(validation.error.flatten().fieldErrors.reason?.[0] ?? null);
        return;
      }
      rejectionReason = validation.data.reason;
    }

    const attachmentUrl =
      externalAttachments[0] && "url" in externalAttachments[0]
        ? externalAttachments[0].url
        : undefined;
    const attachmentName =
      externalAttachments[0] && "name" in externalAttachments[0]
        ? externalAttachments[0].name
        : undefined;
    const internalAttachmentUrl =
      internalAttachments[0] && "url" in internalAttachments[0]
        ? internalAttachments[0].url
        : undefined;
    const internalAttachmentName =
      internalAttachments[0] && "name" in internalAttachments[0]
        ? internalAttachments[0].name
        : undefined;

    await completeShippingAgencyTransfer({
      id: transferId,
      service: SERVICE,
      payload: {
        status: isReject ? "REJECTED" : "APPROVED",
        ...(isReject ? { rejectionReason } : { externalNotes: primaryNote || undefined }),
        internalNotes: internalNotes || undefined,
        externalAttachmentsUrl: attachmentUrl,
        externalAttachmentsName: attachmentName,
        internalAttachmentsUrl: internalAttachmentUrl,
        internalAttachmentsName: internalAttachmentName,
      },
    });
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      title={t(`${LANG_BASE}.${isReject ? "reject" : "approve"}-transfer-title`)}
      submitLabel={t(`${LANG_BASE}.flow-buttons.${isReject ? "reject" : "approve"}-button`)}
      onClose={() => {
        clearForm();
        onClose(drawerName);
      }}
      onSubmit={handleSubmit}
      keepMounted={false}
    >
      <div className="flex-1 max-w-sm">
        <div className="px-6">
          <div className="flex flex-col gap-2">
            <CustomFormLabel>
              {t(`${LANG_BASE}.${isReject ? "rejection-reason" : "approval-notes-to-agent"}`)}
            </CustomFormLabel>
            <TextField
              placeholder={t(
                `${LANG_BASE}.${isReject ? "rejection-reason" : "approval-notes-to-agent"}`,
              )}
              multiline
              minRows={4}
              fullWidth
              value={primaryNote}
              error={isReject && Boolean(error)}
              slotProps={{
                htmlInput: {
                  "data-testid": isReject
                    ? "shipping-agency-transfer-rejection-reason-input"
                    : "shipping-agency-transfer-approval-notes-to-agent-input",
                },
              }}
              onKeyDown={(e) => {
                if (!isReject || !isPrintableKey(e)) return;
                if (primaryNote.length >= REJECTION_REASON_MAX_LENGTH) {
                  validateReason(primaryNote + e.key);
                }
              }}
              onChange={(event) => {
                const nextValue = event.target.value;
                setPrimaryNote(nextValue);
                if (isReject) validateReason(nextValue);
              }}
              onBlur={() => {
                if (isReject) validateReason(primaryNote);
              }}
            />
            {isReject && error && (
              <FormHelperText error data-testid="shipping-agency-transfer-rejection-reason-error">
                {error}
              </FormHelperText>
            )}
            {isReject && (
              <FormHelperText sx={charCounterSx}>
                {primaryNote.length}/{REJECTION_REASON_MAX_LENGTH}
              </FormHelperText>
            )}
          </div>
          <AttachmentField
            fieldName={EXTERNAL_ATTACHMENT_FIELD}
            label={t(`${LANG_BASE}.${isReject ? "rejection" : "approval"}-external-attachment`)}
            testId="shipping-agency-transfer-external-attachment"
            files={externalAttachments}
            setFiles={setExternalAttachments}
            upload={externalAttachmentUploads}
            error={externalAttachmentError}
            setError={setExternalAttachmentError}
            allowedExtensions={allowedExtensions}
            conditions={attachmentProfile}
          />
          <div data-testid="divider" className="my-6 w-full" style={{ borderTop: dividerBorder }} />
          <div className="flex flex-col gap-2">
            <CustomFormLabel>{t(`${LANG_BASE}.internal-notes`)}</CustomFormLabel>
            <TextField
              placeholder={t(`${LANG_BASE}.internal-notes`)}
              multiline
              minRows={4}
              fullWidth
              value={internalNotes}
              slotProps={{
                htmlInput: { "data-testid": "shipping-agency-transfer-internal-notes-input" },
              }}
              onChange={(event) => setInternalNotes(event.target.value)}
            />
          </div>
          <AttachmentField
            fieldName={INTERNAL_ATTACHMENT_FIELD}
            label={t(`${LANG_BASE}.${isReject ? "rejection" : "approval"}-internal-attachment`)}
            testId="shipping-agency-transfer-internal-attachment"
            files={internalAttachments}
            setFiles={setInternalAttachments}
            upload={internalAttachmentUploads}
            error={internalAttachmentError}
            setError={setInternalAttachmentError}
            allowedExtensions={allowedExtensions}
            conditions={attachmentProfile}
          />
        </div>
      </div>
    </SideDrawer>
  );
};

export default ShippingAgencyTransferDecisionDrawer;
